import styled from 'styled-components'
import { HTMLAttributes } from 'react'

/**
 * TextProps - Compatible with rebass Text API subset used in this codebase
 * Supports both shorthand (ml, mr) and long-form (marginLeft, marginRight) props
 * Uses transient props ($prefix) internally for styled-components v6 compatibility
 */
export interface TextProps extends HTMLAttributes<HTMLDivElement> {
  fontWeight?: number | string
  fontSize?: number | string
  color?: string
  fontStyle?: string
  lineHeight?: string | number
  textAlign?: 'left' | 'center' | 'right' | 'justify'
  // Margin props - shorthand (accept string or number)
  ml?: string | number
  mr?: string | number
  mt?: string | number
  mb?: string | number
  mx?: string | number
  my?: string | number
  m?: string | number
  // Margin props - long-form
  margin?: string | number
  marginLeft?: string | number
  marginRight?: string | number
  marginTop?: string | number
  marginBottom?: string | number
  // Padding props - shorthand (accept string or number)
  pl?: string | number
  pr?: string | number
  pt?: string | number
  pb?: string | number
  px?: string | number
  py?: string | number
  p?: string | number
  // Padding props - long-form
  padding?: string | number
  paddingLeft?: string | number
  paddingRight?: string | number
  paddingTop?: string | number
  paddingBottom?: string | number
  // Polymorphic as prop
  as?: React.ElementType
}

// Internal transient props for styled-components
interface StyledTextProps {
  $fontWeight?: number | string
  $fontSize?: number | string
  $color?: string
  $fontStyle?: string
  $lineHeight?: string | number
  $textAlign?: 'left' | 'center' | 'right' | 'justify'
  $ml?: string | number
  $mr?: string | number
  $mt?: string | number
  $mb?: string | number
  $mx?: string | number
  $my?: string | number
  $m?: string | number
  $pl?: string | number
  $pr?: string | number
  $pt?: string | number
  $pb?: string | number
  $px?: string | number
  $py?: string | number
  $p?: string | number
}

// Helper to convert number to spacing (assume rem for numbers)
const toSpacing = (value: string | number | undefined): string | undefined => {
  if (value === undefined) return undefined
  return typeof value === 'number' ? `${value * 0.25}rem` : value
}

const StyledText = styled.div<StyledTextProps>`
  font-weight: ${({ $fontWeight }) => $fontWeight};
  font-size: ${({ $fontSize }) => (typeof $fontSize === 'number' ? `${$fontSize}px` : $fontSize)};
  color: ${({ $color }) => $color};
  font-style: ${({ $fontStyle }) => $fontStyle};
  line-height: ${({ $lineHeight }) => $lineHeight};
  text-align: ${({ $textAlign }) => $textAlign};
  margin: ${({ $m }) => toSpacing($m)};
  margin-left: ${({ $ml, $mx }) => toSpacing($ml) ?? toSpacing($mx)};
  margin-right: ${({ $mr, $mx }) => toSpacing($mr) ?? toSpacing($mx)};
  margin-top: ${({ $mt, $my }) => toSpacing($mt) ?? toSpacing($my)};
  margin-bottom: ${({ $mb, $my }) => toSpacing($mb) ?? toSpacing($my)};
  padding: ${({ $p }) => toSpacing($p)};
  padding-left: ${({ $pl, $px }) => toSpacing($pl) ?? toSpacing($px)};
  padding-right: ${({ $pr, $px }) => toSpacing($pr) ?? toSpacing($px)};
  padding-top: ${({ $pt, $py }) => toSpacing($pt) ?? toSpacing($py)};
  padding-bottom: ${({ $pb, $py }) => toSpacing($pb) ?? toSpacing($py)};
`

/**
 * Text component - Drop-in replacement for rebass Text
 * Converts public props to transient props internally
 */
export const Text = ({
  fontWeight,
  fontSize,
  color,
  fontStyle,
  lineHeight,
  textAlign,
  // Shorthand props
  ml,
  mr,
  mt,
  mb,
  mx,
  my,
  m,
  pl,
  pr,
  pt,
  pb,
  px,
  py,
  p,
  // Long-form props (take precedence over shorthand)
  margin,
  marginLeft,
  marginRight,
  marginTop,
  marginBottom,
  padding,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingBottom,
  ...rest
}: TextProps) => {
  return (
    <StyledText
      $fontWeight={fontWeight}
      $fontSize={fontSize}
      $color={color}
      $fontStyle={fontStyle}
      $lineHeight={lineHeight}
      $textAlign={textAlign}
      $m={margin ?? m}
      $ml={marginLeft ?? ml}
      $mr={marginRight ?? mr}
      $mt={marginTop ?? mt}
      $mb={marginBottom ?? mb}
      $mx={mx}
      $my={my}
      $p={padding ?? p}
      $pl={paddingLeft ?? pl}
      $pr={paddingRight ?? pr}
      $pt={paddingTop ?? pt}
      $pb={paddingBottom ?? pb}
      $px={px}
      $py={py}
      {...rest}
    />
  )
}

export default Text
